import { AxiosResponse } from "axios";
import React, { useState } from "react";
import { ThreeDots } from "react-loader-spinner";
import { UseMutationResult } from "react-query";
import { useKeyboardShortcut } from "src/components/hooks/use.keyboard.shortcut";
import {
  MultiWorkout,
  Workout,
  WorkoutType,
} from "src/modules/fithub-shared/interfaces/workout";
import api from "src/rest/api";
import AerobicComp from "./aerobic";
import CrossfitComp from "./crossfit";
import WorkoutHeader from "./Header";
import HeatingComp from "./heating";
import WeightWorkout from "./weight";

interface WorkoutViewProps {
  workout: Workout;
  unselect: () => void;
  mutation: UseMutationResult<
    AxiosResponse<Workout, any>,
    unknown,
    Workout,
    unknown
  >;
}

const WorkoutView: React.FC<WorkoutViewProps> = ({
  workout: values,
  unselect,
  mutation,
}) => {
  const { id } = values;
  const [workout, setWorkout] = useState<Workout | undefined>(values);

  const [debug, setDebug] = useState(false);

  useKeyboardShortcut([
    {
      keys: ["Control", "Shift", "j"],
      callback: () => setDebug((prev) => !prev),
    },
  ]);

  const onChange = (item: MultiWorkout) => {
    if (!workout) return;
    setWorkout({ ...workout, cache: item });
    saveCache(item);
  };

  function saveCache(over?: MultiWorkout) {
    if (over) {
      api
        .post("/workouts/cache", { id, cache: over })
        .then((res) => {
          mutation.mutate(res.data);
        })
        .catch((error) => console.error("Failed to save cache:", error));
    }
  }

  function eraseCache() {
    setWorkout(undefined);

    api
      .post<Workout>("/workouts/cache-remove", { id })
      .then((res) => {
        setWorkout(res.data);
        mutation.mutate(res.data);
      })
      .catch((error) => console.error("Error erasing cache:", error));
  }

  async function handleSave() {
    if (!workout) return;
    const { id, title, obs, cache: item } = workout;
    try {
      setWorkout(undefined);

      const res = await api.post<Workout>("/workouts/save", {
        id,
        item,
        title,
        obs,
      });
      mutation.mutate(res.data);
      setWorkout(res.data);
    } catch (error) {
      console.error("Error saving workout:", error);
    }
  }

  function handleRename(title: string) {
    if (!workout) return;
    try {
      const changes: Workout = { ...workout, title };
      setWorkout(changes);
      mutation.mutate(changes);
    } catch (error) {
      console.error("Error saving workout:", error);
    }
  }

  function handleObs(obs: string) {
    if (!workout) return;
    try {
      const changes: Workout = { ...workout, obs };
      setWorkout(changes);
      mutation.mutate(changes);
    } catch (error) {
      console.error("Error saving workout:", error);
    }
  }

  const renderWorkoutComponent = () => {
    if (!workout) {
      return (
        <div className="hfcentered flex">
          <ThreeDots
            height="60"
            width="60"
            color="#000"
            ariaLabel="three-dots-loading"
            visible={true}
          />
        </div>
      );
    }

    const GetItem = <T extends MultiWorkout>(): T => {
      return (workout.cache as T) ?? (workout.item as T);
    };

    if (GetItem() === undefined) return <p>Treino Inválido!</p>;

    switch (workout.type) {
      case WorkoutType.weight:
        return (
          <WeightWorkout
            content={GetItem()}
            obs={workout.obs}
            onChange={onChange}
            onChangeObs={handleObs}
          />
        );
      case WorkoutType.aerobic:
        return (
          <AerobicComp
            content={GetItem()}
            obs={workout.obs}
            onChange={onChange}
            onChangeObs={handleObs}
          />
        );
      case WorkoutType.crossfit:
        return (
          <CrossfitComp
            content={GetItem()}
            obs={workout.obs}
            onChange={onChange}
            onChangeObs={handleObs}
          />
        );
      case WorkoutType.heating:
        return (
          <HeatingComp
            content={GetItem()}
            obs={workout.obs}
            onChange={onChange}
            onChangeObs={handleObs}
          />
        );
      default:
        return (
          <div>
            <p>Unknown workout type.</p>
          </div>
        );
    }
  };

  return (
    <>
      <WorkoutHeader
        onRename={handleRename}
        handleSave={workout?.cache && handleSave}
        undo={workout?.cache && eraseCache}
        unselect={unselect}
        title={workout?.title ?? "Carregando..."}
        type={workout?.type ?? 0}
      />
      {renderWorkoutComponent()}
      {debug && (
        <div className="pointer-events-none absolute bottom-0 right-0 top-0 w-[25vw] overflow-y-auto bg-zinc-800 p-4 opacity-70">
          <pre className="pointer-events-none whitespace-pre-wrap text-sm text-white">
            {workout?.cache
              ? JSON.stringify(workout?.cache, (key, value) => value, 2)
              : "NOT SAVED CACHE"}
          </pre>
        </div>
      )}
    </>
  );
};

export default WorkoutView;
